/***
*
*   ADD-ONS PANEL
*   Manage add-on products with CRUD operations
*
**********/

import { useState, useEffect } from 'react';
import { Card, Button, Icon } from 'components/lib';
import Style from './bundlePackaging.tailwind.js';

const PRICING_TYPES = [
  { value: 'per_unit', label: 'Per Unit' },
  { value: 'per_user', label: 'Per User' },
  { value: 'per_org', label: 'Per Organization' },
  { value: 'fixed', label: 'Fixed Price' },
];

const INTERVAL_OPTIONS = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
];

const DEFAULT_ADDON = {
  id: '',
  key: '',
  feature_key: '',
  name: '',
  description: '',
  price: 0,
  price_min: null,
  interval: 'month',
  per_unit: 1,
  unit: '',
  type: 'recurring',
  pricing_type: 'per_unit',
};

export function AddOnsPanel({ addOns, availableFeatures, onSave, onDelete, onCreate, onUpdate, loading }) {
  const [addOnsList, setAddOnsList] = useState({});
  const [editingAddOn, setEditingAddOn] = useState(null);
  const [isNewAddOn, setIsNewAddOn] = useState(false);

  useEffect(() => {
    if (addOns) {
      setAddOnsList(addOns);
    }
  }, [addOns]);

  const handleAddAddOn = () => {
    setEditingAddOn({ ...DEFAULT_ADDON });
    setIsNewAddOn(true);
  };

  const handleEditAddOn = (key, addOn) => {
    setEditingAddOn({ ...addOn, key });
    setIsNewAddOn(false);
  };

  const handleCancelEdit = () => {
    setEditingAddOn(null);
    setIsNewAddOn(false);
  };

  const handleSaveAddOn = () => {
    console.log('d', editingAddOn)
    if (!editingAddOn.key || !editingAddOn.name || !editingAddOn.feature_key) {
      return;
    }

    const addOnData = {
      id: editingAddOn.id || `addon_${editingAddOn.key}`,
      feature_key: editingAddOn.feature_key,
      name: editingAddOn.name,
      description: editingAddOn.description,
      pricing_type: editingAddOn.pricing_type,
    };

    // Add pricing fields based on type
    if (editingAddOn.type === 'one_time') {
      addOnData.type = 'one_time';
      if (editingAddOn.pricing_type === 'fixed') {
        addOnData.price_min = editingAddOn.price_min;
      } else {
        addOnData.price = editingAddOn.price;
      }
    } else {
      addOnData.price = editingAddOn.price;
      addOnData.interval = editingAddOn.interval;
      if (editingAddOn.pricing_type === 'per_unit' || editingAddOn.pricing_type === 'per_user') {
        addOnData.per_unit = editingAddOn.per_unit;
        addOnData.unit = editingAddOn.unit;
      }
    }

    if (isNewAddOn) {
      onCreate({ key: editingAddOn.key, ...addOnData });
    } else {
      onUpdate(editingAddOn.id || editingAddOn.key, { key: editingAddOn.key, ...addOnData });
    }

    setEditingAddOn(null);
    setIsNewAddOn(false);
  };

  const handleDeleteAddOn = (key) => {
    const addOn = addOnsList[key];
    onDelete(addOn.id || key);
  };

  const handleFieldChange = (field, value) => {
    setEditingAddOn(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const addOnKeys = Object.keys(addOnsList);

  const getPricingTypeIcon = (pricingType) => {
    switch (pricingType) {
      case 'per_unit': return 'package';
      case 'per_user': return 'user';
      case 'per_org': return 'briefcase';
      case 'fixed': return 'tag';
      default: return 'dollar-sign';
    }
  };

  const getPricingTypeColor = (pricingType) => {
    switch (pricingType) {
      case 'per_unit': return 'blue';
      case 'per_user': return 'purple';
      case 'per_org': return 'green';
      case 'fixed': return 'orange';
      default: return 'gray';
    }
  };

  const formatPrice = (addOn) => {
    if (addOn.type === 'one_time') {
      if (addOn.price_min) {
        return `From $${addOn.price_min}`;
      }
      return `$${addOn.price || 0}`;
    }
    
    if (addOn.pricing_type === 'per_unit' || addOn.pricing_type === 'per_user') {
      return `$${addOn.price}/${addOn.per_unit || 1} ${addOn.unit || 'unit'}/${addOn.interval}`;
    }
    
    return `$${addOn.price}/${addOn.interval}`;
  };

  return (
    <div className="space-y-6">
      {/* Add Add-on Button */}
      <div className="flex justify-end">
        <Button
          text="Add New Add-on"
          icon="plus"
          color="green"
          action={handleAddAddOn}
        />
      </div>

      {/* Add-on Editor */}
      {editingAddOn && (
        <Card>
          <div className={Style.settingsSection}>
            <h3 className={Style.settingsHeader}>
              {isNewAddOn ? 'Add New Add-on' : 'Edit Add-on'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Basic Info */}
              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Add-on Key *</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingAddOn.key}
                  onChange={(e) => handleFieldChange('key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                  placeholder="e.g., storage, job_opening"
                  disabled={!isNewAddOn}
                />
                <p className="text-xs text-slate-500 mt-1">Unique identifier (snake_case)</p>
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Name *</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingAddOn.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="e.g., Additional Storage"
                />
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Feature Key *</label>
                <select
                  className={Style.settingsInput}
                  value={editingAddOn.feature_key}
                  onChange={(e) => handleFieldChange('feature_key', e.target.value)}
                >
                  <option value="">Select a feature...</option>
                  {availableFeatures?.map(feature => (
                    <option key={feature.key} value={feature.key}>
                      {feature.name} ({feature.key})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Links to plan feature for limits</p>
              </div>

              <div className={Style.settingsCard + " md:col-span-2 lg:col-span-3"}>
                <label className={Style.settingsLabel}>Description</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingAddOn.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="e.g., Increase your file storage quota."
                />
              </div>

              {/* Pricing Configuration */}
              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Billing Type</label>
                <select
                  className={Style.settingsInput}
                  value={editingAddOn.type === 'one_time' ? 'one_time' : 'recurring'}
                  onChange={(e) => handleFieldChange('type', e.target.value)}
                >
                  <option value="recurring">Recurring</option>
                  <option value="one_time">One-time</option>
                </select>
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Pricing Type</label>
                <select
                  className={Style.settingsInput}
                  value={editingAddOn.pricing_type}
                  onChange={(e) => handleFieldChange('pricing_type', e.target.value)}
                >
                  {PRICING_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {editingAddOn.type !== 'one_time' && (
                <div className={Style.settingsCard}>
                  <label className={Style.settingsLabel}>Billing Interval</label>
                  <select
                    className={Style.settingsInput}
                    value={editingAddOn.interval}
                    onChange={(e) => handleFieldChange('interval', e.target.value)}
                  >
                    {INTERVAL_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Price Fields */}
              {editingAddOn.type === 'one_time' && editingAddOn.pricing_type === 'fixed' ? (
                <div className={Style.settingsCard}>
                  <label className={Style.settingsLabel}>Minimum Price ($)</label>
                  <input
                    type="number"
                    className={Style.settingsInput}
                    value={editingAddOn.price_min ?? ''}
                    onChange={(e) => handleFieldChange('price_min', e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="500"
                    step="0.01"
                    min="0"
                  />
                </div>
              ) : (
                <div className={Style.settingsCard}>
                  <label className={Style.settingsLabel}>Price ($)</label>
                  <input
                    type="number"
                    className={Style.settingsInput}
                    value={editingAddOn.price ?? ''}
                    onChange={(e) => handleFieldChange('price', e.target.value ? parseFloat(e.target.value) : 0)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
              )}

              {/* Per Unit Fields */}
              {(editingAddOn.pricing_type === 'per_unit' || editingAddOn.pricing_type === 'per_user') && (
                <>
                  <div className={Style.settingsCard}>
                    <label className={Style.settingsLabel}>Per Unit Amount</label>
                    <input
                      type="number"
                      className={Style.settingsInput}
                      value={editingAddOn.per_unit ?? 1}
                      onChange={(e) => handleFieldChange('per_unit', parseInt(e.target.value) || 1)}
                      placeholder="1"
                      min="1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Amount per unit (e.g., 100 MB)</p>
                  </div>

                  <div className={Style.settingsCard}>
                    <label className={Style.settingsLabel}>Unit Label</label>
                    <input
                      type="text"
                      className={Style.settingsInput}
                      value={editingAddOn.unit}
                      onChange={(e) => handleFieldChange('unit', e.target.value)}
                      placeholder="e.g., MB, job, user"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <Button
                text="Cancel"
                color="gray"
                outline
                action={handleCancelEdit}
              />
              <Button
                text={isNewAddOn ? 'Create Add-on' : 'Save Changes'}
                color="green"
                loading={loading}
                action={handleSaveAddOn}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Add-ons List */}
      <div className={Style.addOnsGrid}>
        {addOnKeys.length > 0 ? (
          addOnKeys.map((key) => {
            const addOn = addOnsList[key];
            const isOneTime = addOn.type === 'one_time';

            return (
              <Card key={key} className={Style.addOnCard}>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className={Style.addOnName}>{addOn.name}</h3>
                    <p className="text-xs text-slate-400 uppercase font-mono">{key}</p>
                    {addOn.description && (
                      <p className="text-sm text-slate-500 mt-1 truncate">{addOn.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      icon="edit-2"
                      size="small"
                      color="blue"
                      outline
                      action={() => handleEditAddOn(key, addOn)}
                    />
                    <Button
                      icon="trash-2"
                      size="small"
                      color="red"
                      outline
                      action={() => handleDeleteAddOn(key)}
                    />
                  </div>
                </div>

                {/* Pricing Info */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={Style.addOnPrice}>{formatPrice(addOn)}</span>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Icon
                        image={isOneTime ? 'zap' : 'refresh-cw'}
                        color={isOneTime ? 'orange' : 'blue'}
                        size={14}
                      />
                      <span>{isOneTime ? 'One-time' : 'Recurring'}</span>
                    </div>
                  </div>

                  {/* Meta Tags */}
                  <div className="flex flex-wrap gap-2">
                    <div className={Style.addOnTag}>
                      <Icon
                        image={getPricingTypeIcon(addOn.pricing_type)}
                        color={getPricingTypeColor(addOn.pricing_type)}
                        size={12}
                      />
                      <span>{PRICING_TYPES.find(t => t.value === addOn.pricing_type)?.label || addOn.pricing_type}</span>
                    </div>
                    
                    {addOn.feature_key && (
                      <div className={Style.addOnTag}>
                        <Icon image="link" color="gray" size={12} />
                        <span>{addOn.feature_key}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        ) : (
          <Card>
            <div className={Style.emptyState}>
              <p className={Style.emptyStateText}>No add-ons configured yet</p>
              <Button
                text="Add Your First Add-on"
                icon="plus"
                color="green"
                action={handleAddAddOn}
              />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}